import { gameStatIcon, STREAK_STAT_KEYS } from '../profile-assets.js';

/**
 * The bottom section of a game card's inset parchment panel (APP_SPEC §13.3)
 * — not a detached beige footer. It shares the panel's surface with the
 * primary stats above it and is separated from them by a single divider, so
 * the whole panel reads as one block of information.
 *
 * Both of its rules are inset from the panel's edges rather than run wall to
 * wall: a full-bleed rule cuts the panel into two stacked boxes, while an
 * inset one reads as a section break within one sheet.
 *
 * Each game has its *own* current/best streak; this is not a global platform
 * streak. The flame and crown are the largest icons on the card after the
 * game emblem, and neither cell is boxed.
 */
export interface GameStreakStatsProps {
  currentDays: number;
  bestDays: number;
}

/**
 * Centred in its half of the row for the same reason the primary stat cells
 * are — two clusters pinned to the left of two wide halves read as an
 * unbalanced row. Number and unit are one typographic value: same serif, same
 * ink, the unit merely smaller, rather than a serif numeral followed by a
 * word in UI sans.
 */
function StreakCell({ iconSrc, label, days }: { iconSrc: string; label: string; days: number }) {
  return (
    // Same two-part structure as `GameStat`: the cell is a centring box on
    // both axes, and the icon + text is a shrink-wrapped group inside it. The
    // group is what gets centred, not a full-width block whose children
    // happen to start at the left edge.
    <div className="flex min-w-0 items-center justify-center px-3 py-4 tablet:px-4">
      <span className="inline-flex w-fit min-w-0 max-w-full items-center gap-3">
        <img
          src={iconSrc}
          alt=""
          aria-hidden="true"
          className="h-10 w-10 shrink-0 object-contain tablet:h-11 tablet:w-11"
        />
        <span className="min-w-0">
          <span className="block font-profile text-[0.9rem] font-semibold leading-tight text-textPrimary tablet:text-[0.95rem]">
            {label}
          </span>
          {/* `days` is part of the value, not a caption beside it: same
              Alegreya, same ink, one step down in size and weight. It must
              never fall back to the UI sans — a serif numeral followed by a
              sans word reads as two different data points. */}
          <span className="mt-0.5 block font-profile leading-[1.1] text-textPrimary [font-variant-numeric:lining-nums_tabular-nums]">
            <span className="text-[1.5rem] font-bold wide:text-[1.6rem]">
              {days.toLocaleString()}
            </span>{' '}
            <span className="text-[1.05rem] font-medium">{days === 1 ? 'day' : 'days'}</span>
          </span>
        </span>
      </span>
    </div>
  );
}

export function GameStreakStats({ currentDays, bestDays }: GameStreakStatsProps) {
  return (
    <div className="shrink-0">
      <div aria-hidden="true" className="mx-3.5 h-px bg-borderStrong/25" />
      {/* Stacked on a phone for the same reason the stat grid is, so the
          divider between the two cells is vertical only where there are in
          fact two columns. */}
      <div className="relative grid grid-cols-1 tablet:grid-cols-2">
        <span
          aria-hidden="true"
          className="absolute inset-x-4 top-1/2 hidden h-px bg-borderStrong/15 tablet:inset-x-auto tablet:inset-y-3.5 tablet:left-1/2 tablet:block tablet:h-auto tablet:w-px tablet:-translate-x-1/2"
        />
        <StreakCell
          iconSrc={gameStatIcon(STREAK_STAT_KEYS.current)}
          label="Current streak"
          days={currentDays}
        />
        <StreakCell
          iconSrc={gameStatIcon(STREAK_STAT_KEYS.best)}
          label="Best streak"
          days={bestDays}
        />
      </div>
    </div>
  );
}
